import React, { useState } from 'react';
import styled from 'styled-components';

// Import Monday.com tutorial screenshots
import CrmStep1 from '../../Images/CRM/CrmStep1.png';
import CrmStep2 from '../../Images/CRM/CrmStep2.png';
import CrmStep3 from '../../Images/CRM/CrmStep3.png';
import CrmStep4 from '../../Images/CRM/CrmStep4.png';

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #1E1E1E;
  color: #FFFFFF;
  border-radius: 12px;
  border: 1px solid rgba(120, 130, 140, 0.3);
  width: min(800px, 95vw);
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid rgba(120, 130, 140, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: #1E1E1E;
  z-index: 1;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #FFFFFF;
  font-size: 24px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: 1px solid rgba(120, 130, 140, 0.3);
  color: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: rgba(120, 130, 140, 0.1);
    color: #FFFFFF;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(120, 130, 140, 0.2);
  margin-bottom: 24px;
`;

const Tab = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.isActive ? '#2f88f5' : 'rgba(255, 255, 255, 0.7)'};
  padding: 12px 24px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 2px solid ${props => props.isActive ? '#2f88f5' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.isActive ? '#2f88f5' : '#FFFFFF'};
  }
`;

const StepContainer = styled.div`
  margin-bottom: 32px;
`;

const StepNumber = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #2f88f5;
  color: white;
  border-radius: 50%;
  font-weight: bold;
  margin-right: 16px;
  font-size: 14px;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const StepTitle = styled.h3`
  margin: 0;
  color: #FFFFFF;
  font-size: 18px;
`;

const StepContent = styled.div`
  margin-left: 48px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
`;

const ScreenshotImage = styled.img`
  width: 100%;
  max-width: 700px;
  border-radius: 8px;
  border: 1px solid rgba(120, 130, 140, 0.3);
  margin: 16px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  display: block;
`;

const CodeBlock = styled.pre`
  background: #0f0f12;
  border: 1px solid rgba(120, 130, 140, 0.3);
  border-radius: 6px;
  padding: 16px;
  color: #e6e8ee;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  margin: 16px 0;
`;

const WarningBox = styled.div`
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
  padding: 16px;
  margin: 16px 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const WarningIcon = styled.div`
  color: #ffc107;
  font-size: 20px;
  margin-top: 2px;
`;

const InfoBox = styled.div`
  background: rgba(47, 136, 245, 0.1);
  border: 1px solid rgba(47, 136, 245, 0.3);
  border-radius: 6px;
  padding: 16px;
  margin: 16px 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const InfoIcon = styled.div`
  color: #2f88f5;
  font-size: 20px;
  margin-top: 2px;
`;

const SuccessBox = styled.div`
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 6px;
  padding: 16px;
  margin: 16px 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const SuccessIcon = styled.div`
  color: #4CAF50;
  font-size: 20px;
  margin-top: 2px;
`;

const LinkButton = styled.button`
  background: transparent;
  border: none;
  color: #2f88f5;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  
  &:hover {
    color: #4a9eff;
  }
`;

const TABS = [
  { id: 'monday', name: 'Setup Steps', icon: '📋' },
  { id: 'troubleshooting', name: 'Troubleshooting', icon: '🔧' },
  { id: 'features', name: 'Features', icon: '✨' }
];

const CRMHelpGuide = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('monday');

  const renderMondaySetup = () => (
    <div>
      <StepContainer>
        <StepHeader>
          <StepNumber>1</StepNumber>
          <StepTitle>Click Your Name</StepTitle>
        </StepHeader>
        <StepContent>
          <p>Log in to Monday.com and click on your name/icon in the top right corner.</p>
          
          <ScreenshotImage 
            src={CrmStep1} 
            alt="Monday.com interface with name/icon in top right corner"
          />
        </StepContent>
      </StepContainer>

      <StepContainer>
        <StepHeader>
          <StepNumber>2</StepNumber>
          <StepTitle>Go to Develop</StepTitle>
        </StepHeader>
        <StepContent>
          <p>In the dropdown menu, click on <strong>"Develop"</strong>.</p>
          
          <ScreenshotImage 
            src={CrmStep2} 
            alt="Dropdown menu showing Develop option"
          />
        </StepContent>
      </StepContainer>

      <StepContainer>
        <StepHeader>
          <StepNumber>3</StepNumber>
          <StepTitle>Open API Token</StepTitle>
        </StepHeader>
        <StepContent>
          <p>In the Develop section, click on <strong>"API token"</strong>.</p>
          
          <ScreenshotImage 
            src={CrmStep3} 
            alt="Develop section with API token menu"
          />
        </StepContent>
      </StepContainer>

      <StepContainer>
        <StepHeader>
          <StepNumber>4</StepNumber>
          <StepTitle>Copy Token</StepTitle>
        </StepHeader>
        <StepContent>
          <p>Click the <strong>"Copy"</strong> button to copy your API token.</p>
          
          <ScreenshotImage 
            src={CrmStep4} 
            alt="API token page with Copy button"
          />
          
          <WarningBox>
            <WarningIcon>🔒</WarningIcon>
            <div>
              Keep your API key secure.
            </div>
          </WarningBox>
        </StepContent>
      </StepContainer>

      <StepContainer>
        <StepHeader>
          <StepNumber>5</StepNumber>
          <StepTitle>Paste in iMaven</StepTitle>
        </StepHeader>
        <StepContent>
          <p>Paste your API key here and click "Continue".</p>
        </StepContent>
      </StepContainer>
    </div>
  );

  const renderTroubleshooting = () => (
    <div>
      <h3 style={{ color: '#FFFFFF', marginBottom: '20px' }}>Common Issues</h3>
      
      <StepContainer>
        <StepHeader>
          <StepNumber>❌</StepNumber>
          <StepTitle>"Invalid API Key" Error</StepTitle>
        </StepHeader>
        <StepContent>
          <ul>
            <li>Double-check that you copied the entire API key</li>
            <li>Make sure there are no extra spaces</li>
            <li>Try copying the token again from Monday.com</li>
          </ul>
        </StepContent>
      </StepContainer>

      <StepContainer>
        <StepHeader>
          <StepNumber>⚠️</StepNumber>
          <StepTitle>"No Boards Found"</StepTitle>
        </StepHeader>
        <StepContent>
          <ul>
            <li>Check that you have at least one active board in Monday.com</li>
            <li>Verify you're logged into the correct account</li>
          </ul>
        </StepContent>
      </StepContainer>

      <StepContainer>
        <StepHeader>
          <StepNumber>🔑</StepNumber>
          <StepTitle>Can't Find "Develop" Menu</StepTitle>
        </StepHeader>
        <StepContent>
          <p>If you don't see "Develop" in the top menu, you can also find your API token in:</p>
          <ul>
            <li>Profile → Admin → Connections → Personal API token</li>
          </ul>
        </StepContent>
      </StepContainer>
    </div>
  );

  const renderFeatures = () => (
    <div>
      <h3 style={{ color: '#FFFFFF', marginBottom: '20px' }}>CRM Integration Features</h3>
      
      <StepContainer>
        <StepHeader>
          <StepNumber>🎯</StepNumber>
          <StepTitle>Lead Sync</StepTitle>
        </StepHeader>
        <StepContent>
          <p>Chat conversations automatically sync to your Monday.com board:</p>
          <ul>
            <li>Contact information (name, email, phone)</li>
            <li>Conversation notes</li>
            <li>Source tracking</li>
          </ul>
        </StepContent>
      </StepContainer>

      <StepContainer>
        <StepHeader>
          <StepNumber>📊</StepNumber>
          <StepTitle>Smart Mapping</StepTitle>
        </StepHeader>
        <StepContent>
          <p>Data automatically maps to your board columns:</p>
          <ul>
            <li><strong>Email columns</strong> → Email addresses</li>
            <li><strong>Phone columns</strong> → Phone numbers</li>
            <li><strong>Text columns</strong> → Names, companies</li>
            <li><strong>Status columns</strong> → Lead status</li>
          </ul>
        </StepContent>
      </StepContainer>

      <StepContainer>
        <StepHeader>
          <StepNumber>🔒</StepNumber>
          <StepTitle>Security</StepTitle>
        </StepHeader>
        <StepContent>
          <ul>
            <li>API keys stored securely</li>
            <li>Only adds new leads - never modifies existing data</li>
            <li>Disconnect anytime from profile settings</li>
          </ul>
        </StepContent>
      </StepContainer>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'monday':
        return renderMondaySetup();
      case 'troubleshooting':
        return renderTroubleshooting();
      case 'features':
        return renderFeatures();
      default:
        return renderMondaySetup();
    }
  };

  return (
    <Modal onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>CRM Integration Guide</ModalTitle>
          <CloseButton onClick={onClose}>Close</CloseButton>
        </ModalHeader>

        <ModalBody>
          <TabContainer>
            {TABS.map(tab => (
              <Tab
                key={tab.id}
                isActive={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon} {tab.name}
              </Tab>
            ))}
          </TabContainer>

          {renderTabContent()}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CRMHelpGuide;
